import { useState, useMemo } from 'react'
import { FileText, Search, AlertTriangle } from 'lucide-react'
import { useQuery } from '@tanstack/react-query'
import pb from '@/lib/pb'
import { COLLECTIONS } from '@/types'
import type { TAuditLog } from '@/types'
import { usePbSubscription } from '@/lib/usePbSubscription'
import { formatRelativeTime } from '@/lib/utils'
import { Skeleton, EmptyState } from '@/components/ui'

// ── Data hook ──────────────────────────────────────────────────────────────

function useAuditLogPage() {
  usePbSubscription(COLLECTIONS.AUDIT_LOG, ['audit-log-page'])
  return useQuery({
    queryKey: ['audit-log-page'],
    queryFn: async () => {
      const records = await pb.collection(COLLECTIONS.AUDIT_LOG).getList<TAuditLog>(1, 50, {
      })
      return records.items
    },
  })
}

// ── Filters ────────────────────────────────────────────────────────────────

const ACTION_FILTERS = ['All', 'Provision', 'Health', 'Update', 'Tenant', 'Settings'] as const
type ActionFilter = (typeof ACTION_FILTERS)[number]

// ── Skeleton Row ───────────────────────────────────────────────────────────

function SkeletonRow() {
  return (
    <div style={{
      display: 'grid',
      gridTemplateColumns: '1.2fr 1fr 1fr 0.8fr 1.5fr',
      alignItems: 'center',
      padding: '12px 16px',
      borderBottom: '1px solid var(--border-subtle)',
      gap: 12,
    }}>
      <Skeleton width={90} height={12} />
      <Skeleton width={80} height={14} />
      <Skeleton width={100} height={14} />
      <Skeleton width={60} height={12} />
      <Skeleton width={140} height={12} />
    </div>
  )
}

// ── Audit Page ─────────────────────────────────────────────────────────────

export default function AuditPage() {
  const { data: entries = [], isLoading, isError } = useAuditLogPage()
  const [activeFilter, setActiveFilter] = useState<ActionFilter>('All')
  const [searchQuery, setSearchQuery] = useState('')

  const filteredEntries = useMemo(() => {
    let result = entries

    if (activeFilter !== 'All') {
      const filterLower = activeFilter.toLowerCase()
      result = result.filter(entry => entry.event.toLowerCase().includes(filterLower))
    }

    if (searchQuery.trim()) {
      const q = searchQuery.trim().toLowerCase()
      result = result.filter(entry =>
        entry.actor.toLowerCase().includes(q) ||
        entry.event.toLowerCase().includes(q) ||
        entry.details.toLowerCase().includes(q)
      )
    }

    return result
  }, [entries, activeFilter, searchQuery])

  const columnHeaders = ['Timestamp', 'Actor', 'Event', 'Tenant', 'Details']

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <div style={{ flex: 1, overflow: 'auto', padding: 24 }}>

        {isError && (
          <div style={{
            display: 'flex', alignItems: 'center', gap: 8,
            padding: '12px 16px', marginBottom: 16,
            backgroundColor: 'var(--status-unhealthy-dim)',
            border: '1px solid var(--status-unhealthy)',
            borderRadius: 'var(--radius-md)',
            fontSize: 13, color: 'var(--status-unhealthy)',
          }}>
            <AlertTriangle size={16} />
            Failed to load audit log.
          </div>
        )}

        {/* Filter Bar */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20, flexWrap: 'wrap' }}>
          {ACTION_FILTERS.map(filter => (
            <button
              key={filter}
              className={`filter-chip${activeFilter === filter ? ' active' : ''}`}
              onClick={() => setActiveFilter(filter)}
              style={{
                padding: '6px 14px',
                fontSize: 12,
                fontWeight: 500,
                fontFamily: 'var(--font-sans)',
                backgroundColor: activeFilter === filter ? 'var(--brand-dim)' : 'var(--bg-tertiary)',
                color: activeFilter === filter ? 'var(--brand)' : 'var(--text-secondary)',
                border: `1px solid ${activeFilter === filter ? 'var(--brand)' : 'var(--border-subtle)'}`,
                borderRadius: 'var(--radius-full)',
                cursor: 'pointer',
              }}
            >
              {filter}
            </button>
          ))}

          <div style={{ flex: 1 }} />

          <div style={{
            display: 'flex', alignItems: 'center', gap: 8,
            padding: '6px 12px',
            backgroundColor: 'var(--bg-tertiary)',
            border: '1px solid var(--border-subtle)',
            borderRadius: 'var(--radius-md)',
            minWidth: 200,
          }}>
            <Search size={14} style={{ color: 'var(--text-tertiary)', flexShrink: 0 }} />
            <input
              className="input-field"
              type="text"
              placeholder="Search logs..."
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              style={{
                flex: 1, background: 'transparent', border: 'none', outline: 'none',
                fontSize: 13, color: 'var(--text-primary)', fontFamily: 'var(--font-sans)', padding: 0,
              }}
            />
          </div>
        </div>

        {/* Audit Table */}
        <div style={{
          backgroundColor: 'var(--bg-secondary)',
          border: '1px solid var(--border-subtle)',
          borderRadius: 'var(--radius-lg)',
          overflow: 'hidden',
          boxShadow: 'var(--shadow-card)',
        }}>
          <div style={{
            display: 'grid',
            gridTemplateColumns: '1.2fr 1fr 1fr 0.8fr 1.5fr',
            alignItems: 'center',
            padding: '12px 16px',
            backgroundColor: 'var(--bg-tertiary)',
            borderBottom: '1px solid var(--border-subtle)',
            gap: 12,
          }}>
            {columnHeaders.map(header => (
              <div key={header} style={{
                fontSize: 11, fontWeight: 600, color: 'var(--text-tertiary)',
                textTransform: 'uppercase', letterSpacing: '0.05em', userSelect: 'none',
              }}>
                {header}
              </div>
            ))}
          </div>

          {isLoading ? (
            <>{Array.from({ length: 6 }).map((_, i) => <SkeletonRow key={i} />)}</>
          ) : filteredEntries.length === 0 ? (
            <EmptyState
              icon={<FileText size={32} />}
              title="No audit entries yet"
              description="Actions will be logged here as you use Zeus."
            />
          ) : (
            filteredEntries.map(entry => (
              <AuditRow key={entry.id} entry={entry} />
            ))
          )}
        </div>
      </div>
    </div>
  )
}

// ── Audit Row ──────────────────────────────────────────────────────────────

function AuditRow({ entry }: { entry: TAuditLog }) {
  return (
    <div className="table-row" style={{
      display: 'grid',
      gridTemplateColumns: '1.2fr 1fr 1fr 0.8fr 1.5fr',
      alignItems: 'center',
      padding: '12px 16px',
      borderBottom: '1px solid var(--border-subtle)',
      gap: 12,
    }}>
      <div style={{ fontSize: 12, fontFamily: 'var(--font-mono)', color: 'var(--text-tertiary)', fontVariantNumeric: 'tabular-nums' }}>
        {formatRelativeTime(entry.timestamp || entry.created)}
      </div>
      <div style={{ fontSize: 13, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {entry.actor}
      </div>
      <div style={{ fontSize: 13, color: 'var(--text-secondary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {entry.event}
      </div>
      <div style={{ fontSize: 12, color: 'var(--text-tertiary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {entry.tenant || '\u2014'}
      </div>
      <div style={{ fontSize: 12, color: 'var(--text-tertiary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 200 }} title={entry.details}>
        {entry.details || '\u2014'}
      </div>
    </div>
  )
}
